import { z } from 'zod';

export const MAX_LISTENERS = 100;
export const MAX_ACTIVATION_DEVICES = 3;
export const MAX_ACTIVE_INVITES = 1000;
export const INVITE_TTL_SECONDS = 86_400;
export const INVITE_TTL_OPTIONS = [
  { seconds: 86_400, label: '1 天' },
  { seconds: 604_800, label: '1 周' },
  { seconds: 2_592_000, label: '1 个月（30 天）' }
] as const;
export type InviteTtlSeconds = 86_400 | 259_200 | 604_800 | 2_592_000;
export const ACTIVATION_PLAN_OPTIONS = [
  { ttlSeconds: 86_400, priceUsdt: 10, label: '1 天' },
  { ttlSeconds: 604_800, priceUsdt: 50, label: '1 周' },
  { ttlSeconds: 2_592_000, priceUsdt: 100, label: '1 个月' }
] as const satisfies ReadonlyArray<{ ttlSeconds: InviteTtlSeconds; priceUsdt: number; label: string }>;
export const RESUME_GRACE_SECONDS = 120;
export const AUDIT_RETENTION_DAYS = 30;

export const ErrorCodeSchema = z.enum([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'INVITE_EXPIRED',
  'INVITE_USED',
  'DEVICE_BANNED',
  'RATE_LIMITED',
  'STREAM_OFFLINE',
  'SESSION_ENDED',
  'NICKNAME_TAKEN',
  'PAYMENT_BANNED',
  'PAYMENT_ORDER_DAILY_LIMIT',
  'PAID_MODE_DISABLED',
  'FREE_CLAIM_UNAVAILABLE',
  'FREE_CLAIM_COOLDOWN',
  'INVITE_DEVICE_LIMIT',
  'INVITE_DEVICE_ONLINE',
  'LIMIT_REACHED',
  'INTERNAL_ERROR'
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export interface ApiError {
  code: ErrorCode;
  message: string;
  retryAfterSeconds?: number;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError };

export interface OwnerSession {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
  refreshExpiresAt: string;
}

export interface OwnerLoginRequest {
  username: string;
  password: string;
  totp: string;
}

export interface OwnerBootstrapRequest {
  bootstrapToken: string;
  username: string;
  password: string;
}

export interface OwnerBootstrapResult {
  totpSecret: string;
  otpauthUrl: string;
  recoveryCodes: string[];
  session: OwnerSession;
}

export interface StreamStartResult {
  roomId: string;
  sessionId: string;
  sdkAppId: number;
  userId: string;
  userSig: string;
  privateMapKey: string;
  startedAt: string;
}

export interface InviteView {
  inviteId: string;
  code: string;
  expiresAt: string;
  ttlSeconds: InviteTtlSeconds;
}

export interface InviteSummary {
  inviteId: string;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'redeemed' | 'revoked' | 'expired';
  deviceIdShort?: string;
  activeDeviceIdShort?: string;
  deviceCount: number;
  deviceLimit: number;
  nickname?: string;
  code?: string;
  source: 'owner' | 'purchase' | 'free';
  orderId?: string;
}

export interface ActivationOrderView {
  orderId: string;
  status: 'pending' | 'paid' | 'expired';
  ttlSeconds: InviteTtlSeconds;
  baseAmountUsdt: string;
  payableAmountUsdt: string;
  receivingAddress: string;
  network: 'TRON';
  token: 'USDT';
  createdAt: string;
  expiresAt: string;
  paidAt?: string;
  txId?: string;
  activationCode?: string;
  activationExpiresAt?: string;
}

export interface ActivationOrderAdminView extends ActivationOrderView {
  deviceIdShort: string;
}

export type FreeActivationPromotionState = 'active' | 'before' | 'ended' | 'disabled' | 'cooldown' | 'claimed' | 'ip-claimed';

export interface FreeActivationStatusView {
  state: FreeActivationPromotionState;
  startsAt: string;
  endsAt: string;
  canClaim: boolean;
  nextAvailableAt?: string;
}

export interface FreeActivationClaimView {
  code: string;
  expiresAt: string;
  ttlSeconds: 259_200;
  claimedAt: string;
}

export interface ActivationPaymentEventView {
  txId: string;
  fromAddress: string;
  amountUsdt: string;
  tokenAddress: string;
  blockTimestamp: number;
  reason: 'unsupported_token' | 'amount_or_order_not_found' | 'outside_payment_window' | 'already_processed';
  createdAt: string;
}

export interface StreamView {
  sessionId: string;
  roomId: string;
  status: 'active' | 'waiting' | 'prepared';
  matchId?: string;
  captureMode?: 'system' | 'tab';
  startedAt: string;
  listenerCount: number;
  match?: ScheduleMatch;
}

export interface ListenerRedeemRequest {
  code: string;
  deviceId: string;
  nickname: string;
  streamId?: string;
}

export interface ListenerGrant {
  sessionId: string;
  sessionSecret: string;
  streamId: string;
  sdkAppId: number;
  roomId: string;
  userId: string;
  ownerUserId: string;
  privateMapKey: string;
  userSig: string;
  resumeUntil?: string;
  activationExpiresAt?: string;
  streamStartedAt: string;
  roomStatus: 'active' | 'waiting';
  waitingReason?: 'not-started' | 'stop-grace';
}

export interface ListenerHeartbeatRequest {
  sessionId: string;
  sessionSecret: string;
  latencyMs?: number;
  playing: boolean;
}

export interface ListenerState {
  sessionId: string;
  status: 'active' | 'paused' | 'waiting' | 'ended';
  streamId?: string;
  roomId?: string;
  userSig?: string;
  privateMapKey?: string;
  resumeUntil?: string;
  activationExpiresAt?: string;
  waitingReason?: 'not-started' | 'stop-grace';
  endReason?: string;
}

export interface DisplaySettingsView {
  virtualOnlineBase: number;
  virtualOnlineCount: number;
  nextUpdateAt: string;
  scrollingMessage: string;
  paidActivationEnabled: boolean;
  activationPriceDay: number;
  activationPriceWeek: number;
  activationPriceMonth: number;
  freeActivationEnabled: boolean;
  freeActivationStartDate: string;
  freeActivationEndDate: string;
}

export type ParticipantConnectionState = 'connected' | 'unstable' | 'resuming' | 'paused';

export interface ParticipantView {
  sessionId: string;
  userId: string;
  nickname: string;
  deviceIdShort: string;
  joinedAt: string;
  lastSeenAt: string;
  latencyMs?: number;
  connectionState: ParticipantConnectionState;
  banned: boolean;
}

export type ScheduleBroadcastType = 'foreign' | 'chinese';

export interface ScheduleMatch {
  id: string;
  startsAt: string;
  league: string;
  homeTeam: string;
  awayTeam: string;
  broadcastType: ScheduleBroadcastType;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleSuggestion {
  value: string;
  useCount: number;
  lastUsedAt: string;
}

export interface ScheduleSuggestions {
  leagues: ScheduleSuggestion[];
  teams: ScheduleSuggestion[];
}

export interface ScheduleMatchInput {
  startsAt: string;
  league: string;
  homeTeam: string;
  awayTeam: string;
  broadcastType?: ScheduleBroadcastType;
}

export interface AdminState {
  streamActive: boolean;
  roomId?: string;
  startedAt?: string;
  streams: StreamView[];
  displaySettings: DisplaySettingsView;
  participants: ParticipantView[];
  invites: InviteSummary[];
  activeInviteCount: number;
  usage: {
    monthParticipantMinutes: number;
    monthlyLimitMinutes: number;
    warningThresholdPercent: number;
    limitReached: boolean;
  };
}

export interface AdminParticipantActionRequest {
  reason?: string;
}

export interface TrtcControlCommand {
  type: 'pause' | 'resume' | 'kick';
  targetUserId?: string;
  issuedAt: string;
}

export interface OwnerStatusResult {
  initialized: boolean;
}

export interface HealthResult {
  status: 'ok';
  serverTime: string;
}

const HexIdSchema = z.string().min(8).max(128).regex(/^[a-zA-Z0-9_-]+$/);
const IsoDateSchema = z.string().datetime({ offset: true });

export const OwnerLoginRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(12).max(256),
  totp: z.string().min(6).max(32)
});

export const OwnerBootstrapRequestSchema = z.object({
  bootstrapToken: z.string().min(24).max(256),
  username: z.string().min(3).max(64).regex(/^[a-zA-Z0-9_.-]+$/),
  password: z.string().min(12).max(256)
});

export const InviteTtlSecondsSchema = z.union([
  z.literal(86_400),
  z.literal(604_800),
  z.literal(2_592_000)
]);

export const InviteCreateRequestSchema = z.object({
  ttlSeconds: InviteTtlSecondsSchema.optional()
});

export const ActivationOrderCreateRequestSchema = z.object({
  ttlSeconds: InviteTtlSecondsSchema,
  deviceId: HexIdSchema
});

export const FreeActivationClaimRequestSchema = z.object({
  deviceId: HexIdSchema
});

export const ListenerRedeemRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
  deviceId: HexIdSchema,
  nickname: z.string().trim().min(1).max(24),
  streamId: z.string().min(8).max(128).optional()
});

export const StreamStartRequestSchema = z.object({
  matchId: z.string().min(8).max(128),
  captureMode: z.enum(['system', 'tab'])
});

export const ListenerHeartbeatRequestSchema = z.object({
  sessionId: z.string().uuid(),
  sessionSecret: z.string().min(32).max(256),
  latencyMs: z.any().optional().transform((value): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    return Math.min(60_000, Math.max(0, Math.round(value)));
  }),
  playing: z.boolean()
});

export const ListenerLeaveRequestSchema = z.object({
  sessionId: z.string().uuid(),
  sessionSecret: z.string().min(32).max(256)
});

export const ParticipantIdSchema = z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/);
export const InviteIdSchema = z.string().uuid();
export const AdminReasonSchema = z.object({ reason: z.string().trim().max(160).optional() });
export const ScheduleBroadcastTypeSchema = z.enum(['foreign', 'chinese']);
export const ScheduleMatchInputSchema = z.object({
  startsAt: IsoDateSchema,
  league: z.string().trim().min(1).max(40),
  homeTeam: z.string().trim().min(1).max(40),
  awayTeam: z.string().trim().min(1).max(40),
  broadcastType: ScheduleBroadcastTypeSchema.optional().default('foreign')
});
export const UsageLimitUpdateRequestSchema = z.object({ monthlyLimitMinutes: z.number().int().min(0).max(1_000_000) });
export const CalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day!));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day;
}, '日期格式必须为 YYYY-MM-DD');
export const DisplaySettingsUpdateRequestSchema = z.object({
  virtualOnlineBase: z.number().int().min(0).max(1_000_000).optional(),
  scrollingMessage: z.string().trim().max(200).optional(),
  paidActivationEnabled: z.boolean().optional(),
  activationPriceDay: z.number().int().min(1).max(1_000_000).optional(),
  activationPriceWeek: z.number().int().min(1).max(1_000_000).optional(),
  activationPriceMonth: z.number().int().min(1).max(1_000_000).optional(),
  freeActivationEnabled: z.boolean().optional(),
  freeActivationStartDate: CalendarDateSchema.optional(),
  freeActivationEndDate: CalendarDateSchema.optional()
}).refine((value) => Object.values(value).some((item) => item !== undefined), {
  message: '至少提供一项显示设置'
});
export const IsoDateStringSchema = IsoDateSchema;

export function createApiSuccess<T>(data: T): ApiResult<T> {
  return { ok: true, data };
}

export function createApiFailure(code: ErrorCode, message: string, retryAfterSeconds?: number): ApiResult<never> {
  return {
    ok: false,
    error: retryAfterSeconds === undefined ? { code, message } : { code, message, retryAfterSeconds }
  };
}









